import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState, SectionScaffold, TablePageShell } from '@/shared/ui';

import { useAttributeGroups, useCatalogLanguages, useDeleteAttributeGroup } from '../../hooks';
import { resolveLocalizedText } from '../../lib/localizedText';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { useToast } from '../../lib/useToast';
import { AttributeGroupFormDialog } from '../components/AttributeGroupFormDialog';

/** Attribute groups — the display grouping dictionary attributes reference (plan-catalog-009). */
export function AttributeGroupsPage() {
  const { t, i18n } = useTranslation('catalog');
  const { canStructureView, canStructureManage } = useCatalogPermissions();
  const { toast, success, error: notifyError, clear } = useToast();

  const [filter, setFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [deletingCode, setDeletingCode] = useState<string | null>(null);

  const groupsQuery = useAttributeGroups({ enabled: canStructureView });
  const languagesQuery = useCatalogLanguages({ enabled: canStructureView });
  const deleteMutation = useDeleteAttributeGroup();

  const allGroups = useMemo(() => groupsQuery.data ?? [], [groupsQuery.data]);
  // Content-default locale (adr-tenant-005) — preferred over the portal's own UI language when
  // resolving a translated label (see `resolveLocalizedText`'s resolution-order doc).
  const defaultLocale = useMemo(
    () => languagesQuery.data?.find((l) => l.defaultLocale)?.locale,
    [languagesQuery.data],
  );
  const groups = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return allGroups;
    return allGroups.filter((group) => {
      const label = resolveLocalizedText(group.translations, 'label', i18n.language, '', defaultLocale);
      return group.code.toLowerCase().includes(needle) || label.toLowerCase().includes(needle);
    });
  }, [allGroups, filter, i18n.language, defaultLocale]);

  const editing = editingCode ? (allGroups.find((g) => g.code === editingCode) ?? null) : null;
  const deleting = deletingCode ? (allGroups.find((g) => g.code === deletingCode) ?? null) : null;

  const openCreate = () => {
    setEditingCode(null);
    setDialogOpen(true);
  };
  const openEdit = (code: string) => {
    setEditingCode(code);
    setDialogOpen(true);
  };
  const confirmDelete = () => {
    if (!deleting) return;
    const { code } = deleting;
    deleteMutation.mutate(code, {
      onSuccess: () => {
        success(t('attributeGroups.delete.success', { code }));
        setDeletingCode(null);
      },
      onError: (e) => {
        if (e.code === 'ATTRIBUTE_GROUP_IN_USE') {
          notifyError(t('attributeGroups.delete.errorInUse'));
        } else {
          notifyError(e.message ?? t('attributeGroups.delete.error'));
        }
        setDeletingCode(null);
      },
    });
  };

  return (
    <SectionScaffold
      title={t('attributeGroups.title')}
      subtitle={t('attributeGroups.subtitle')}
      canView={canStructureView}
      settingsTo="/settings/catalog"
      settingsLabel={t('attributeGroups.settingsLabel')}
      search={{ value: filter, onChange: setFilter, placeholder: t('attributeGroups.searchPlaceholder') }}
      primaryAction={
        canStructureManage ? (
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
            {t('attributeGroups.actions.add')}
          </Button>
        ) : undefined
      }
    >
      <TablePageShell>
        {groupsQuery.isLoading ? (
          <LoadingState label={t('attributeGroups.loading')} />
        ) : groupsQuery.isError ? (
          <ErrorState
            title={t('attributeGroups.loadError.title')}
            message={groupsQuery.error?.message ?? t('common:error.unknown')}
            onRetry={() => void groupsQuery.refetch()}
          />
        ) : groups.length === 0 ? (
          <EmptyState
            title={filter ? t('common:empty.noResults') : t('attributeGroups.empty.title')}
            description={
              filter ? t('attributeGroups.empty.filteredDescription') : t('attributeGroups.empty.description')
            }
            icon={<FolderRoundedIcon />}
            action={
              canStructureManage && !filter ? (
                <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
                  {t('attributeGroups.actions.add')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('attributeGroups.table.label')}</TableCell>
                  <TableCell>{t('attributeGroups.table.code')}</TableCell>
                  <TableCell align="right">{t('attributeGroups.table.sortOrder')}</TableCell>
                  <TableCell>{t('attributeGroups.table.status')}</TableCell>
                  <TableCell align="right">{t('attributeGroups.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((group) => (
                  <TableRow key={group.code} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {resolveLocalizedText(
                          group.translations,
                          'label',
                          i18n.language,
                          group.code,
                          defaultLocale,
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        component="code"
                        sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}
                      >
                        {group.code}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{group.sortOrder}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={group.active ? 'success' : 'default'}
                        label={
                          group.active
                            ? t('attributeGroups.statusActive')
                            : t('attributeGroups.statusInactive')
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={
                          canStructureManage
                            ? t('attributeGroups.rowActions.edit')
                            : t('attributeGroups.rowActions.view')
                        }
                      >
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label={t('attributeGroups.rowActions.editAria', { code: group.code })}
                          onClick={() => openEdit(group.code)}
                        >
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canStructureManage && (
                        <Tooltip title={t('attributeGroups.rowActions.delete')}>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={t('attributeGroups.rowActions.deleteAria', { code: group.code })}
                            onClick={() => setDeletingCode(group.code)}
                          >
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TablePageShell>

      <Dialog open={deletingCode !== null} onClose={() => setDeletingCode(null)}>
        <DialogTitle>{t('attributeGroups.delete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('attributeGroups.delete.body', { code: deleting?.code })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingCode(null)}>{t('common:action.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleteMutation.isPending}
          >
            {t('common:action.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <AttributeGroupFormDialog
        open={dialogOpen}
        editing={editing}
        languages={languagesQuery.data ?? []}
        canManage={canStructureManage}
        onClose={() => setDialogOpen(false)}
        onResult={(severity, message) =>
          severity === 'success' ? success(message) : notifyError(message)
        }
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={clear}
          severity={toast?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </SectionScaffold>
  );
}
